import { Component, ElementRef, EventEmitter, inject, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { SettlementsService } from 'src/app/services/settlements.service';
import { PaymentsService } from 'src/app/services/payments.service';
import { STORAGE_KEYS, StorageService } from 'src/app/services/storage.service';
import { IconsService } from 'src/app/shared/services/icons.service';
import {
  buildPaymentComplementPayload,
  toLocalDateInput,
} from 'src/app/shared/utils/payment-complement.util';
import * as xml2js from 'xml2js';
import { firstValueFrom } from 'rxjs';

interface ComplementPayment {
  id: number;
  sat_code: string;
  description: string;
  show_for_payment_complements: boolean;
}

@Component({
  selector: 'app-modal-invoice-complement',
  template: `
    <ng-template #modalInvoiceComplement let-modal>
      <div class="modal-header">
        <h5 class="modal-title">Complemento de pago</h5>
        <button type="button" aria-label="Cerrar" class="btn btn-link text-dark" (click)="dismiss()" [disabled]="loading">
          <fa-icon [icon]="icons.faTimes"></fa-icon>
        </button>
      </div>
      <div class="modal-body p-4">
        <div class="list-group">
          <div class="list-group-item py-3">
          <table class="table table-sm mb-3" style="font-size: 0.8em;">
              <thead>
                <tr>
                  <th>Qty.</th>
                  <th class="text-center">Clave</th>
                  <th class="text-center">Importe</th>
                  <th class="text-end">Valor Unitario</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let concepto of conceptos">
                  <td>{{concepto['$']['Cantidad']}}</td>
                  <td class="text-center">{{concepto['$']['ClaveProdServ']}}</td>
                  <td class="text-center">{{concepto['$']['Importe'] | currency}}</td>
                  <td class="text-end">{{concepto['$']['ValorUnitario'] | currency}}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="list-group-item py-3">
            <form [formGroup]="invoiceComplementForm" (ngSubmit)="createComplement()">
              <div class="form-group mb-3">
                <label for="payment_method" class="form-label text-sm">Forma de pago</label>
                <select id="payment_method" class="form-select" formControlName="payment_method">
                  <option [ngValue]="null" disabled>Seleccionar forma de pago</option>
                  <option *ngFor="let payment of complementPayments" [ngValue]="payment.id">{{payment.description}}</option>
                </select>
                <div *ngIf="invoiceComplementForm.get('payment_method')?.touched && invoiceComplementForm.get('payment_method')?.hasError('required')" class="text-danger text-sm">
                  La forma de pago es requerida.
                </div>
              </div>
              <div class="form-group">
                <label for="payment_date" class="form-label text-sm">Fecha de pago</label>
                <input type="date" class="form-control" formControlName="payment_date">
              </div>
              <div class="form-group text-end mt-4">
                <button type="submit" class="btn btn-sm btn-outline-success" [disabled]="loading">
                  <fa-icon [icon]="icons.faSave" *ngIf="!loading" class="me-2"></fa-icon>
                  <fa-icon [icon]="icons.faCircleNotch" animation="spin" *ngIf="loading" class="me-2"></fa-icon>
                  Generar complemento
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ng-template>
  `,
  styleUrls: []
})
export class ModalInvoiceComplementComponent {
  private readonly store = inject(SettlementsService);
  private readonly paymentsService = inject(PaymentsService);
  private readonly toastr = inject(ToastrService);
  private readonly fb = inject(FormBuilder);
  private readonly modalService = inject(NgbModal);
  public readonly icons = inject(IconsService);

  @Input() profile: any;
  @Input() invoice: any;
  @Input() bill: any;
  @Input() conceptos: any;
  @Input() receptor: any;
  @Input() transaction: any;
  @Output() updateDismiss: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('modalInvoiceComplement') modalInvoiceComplement !: ElementRef;

  private modalRef: NgbModalRef | undefined;

  loading: boolean = false;
  invoiceData: any = {};
  invoiceComplementForm: FormGroup = new FormGroup({});
  complementPayments: ComplementPayment[] = [];
  parser: any;

  storageService = inject(StorageService);
  constructor() {
    this.profile = this.storageService.getItem(STORAGE_KEYS.PROFILE).profile;
    this.buildInvoiceComplementForm();
  }

  buildInvoiceComplementForm() {
    this.invoiceComplementForm = this.fb.group({
      payment_method: [null, Validators.required],
      payment_date: [toLocalDateInput(), [Validators.required]],
    });
  }

  loadComplementPayments(): void {
    this.paymentsService.getComplementsPayments().subscribe({
      next: (payments: ComplementPayment[]) => {
        this.complementPayments = payments;
      },
      error: () => {
        this.toastr.error('No fue posible cargar las formas de pago.', 'Error');
      },
    });
  }

  async createComplement(): Promise<void> {
    if (this.invoiceComplementForm.invalid) {
      this.invoiceComplementForm.markAllAsTouched();
      this.toastr.error('Por favor, completa el formulario.');
      return;
    }

    this.loading = true;
    let complementCreated = false;
    try {
      const response: any = await firstValueFrom(this.store.getInvoice(this.invoice.id));
      const parsedInvoice = await this.processXML(response.invoice.cfdixml);
      const payload = buildPaymentComplementPayload({
        bill: this.bill,
        invoice: response.invoice,
        profile: this.profile,
        parsedInvoice,
        receptor: this.receptor,
        relatedInvoices: response.related,
        paymentDate: this.invoiceComplementForm.value.payment_date,
        paymentAmount: this.transaction?.amount ?? this.bill.total,
        paymentMethod: this.complementPayments.find(
          payment => payment.id === this.invoiceComplementForm.value.payment_method
        )?.sat_code,
      });

      this.invoiceData = await firstValueFrom(this.store.createInvoiceComplement(payload));
      complementCreated = true;
      await this.download();
      this.toastr.success('Complemento generado correctamente.', 'Éxito');
      this.dismiss();
    } catch (error: any) {
      const detail = this.errorDetail(error);
      if (complementCreated) {
        this.toastr.warning(
          `El complemento fue generado, pero no fue posible finalizar su PDF.${detail}`,
          'Complemento generado'
        );
        this.dismiss();
      } else {
        this.toastr.error(`Ocurrió un error al generar el complemento.${detail}`, 'Error');
      }
    } finally {
      this.loading = false;
    }
  }

  async download(): Promise<void> {
    const unitId = this.bill?.unit_id;
    if (!unitId) {
      throw new Error('El recibo no tiene una unidad asociada.');
    }

    const unitData: any = await firstValueFrom(this.store.getUnit(unitId));
    const floorResponse: any = await firstValueFrom(this.store.getFloor(unitData.floor_id));
    const floor = floorResponse?.data?.attributes ?? floorResponse?.attributes ?? floorResponse;
    const buildingId = floor?.building_id ?? floor?.building?.id ?? this.bill?.building?.id;
    if (!buildingId) {
      throw new Error('No fue posible identificar el edificio del recibo.');
    }
    const buildingData: any = await firstValueFrom(this.store.getBuilding(buildingId));
    const pdfData: any = await firstValueFrom(this.store.downloadComplement({
      bill: this.bill,
      dueDate: new Date(this.bill.due_date),
      unitData,
      buildingData,
      invoiceData: this.invoiceData,
      utilityData: this.profile,
    }));

    this.downloadFile(
      new Blob([new Uint8Array(pdfData.result.data)], { type: 'application/pdf' }),
      pdfData.filename
    );
    this.downloadFile(
      new Blob([this.invoiceData.cfdixml], { type: 'application/xml' }),
      `COMPLEMENTO_${this.invoiceData.id}.xml`
    );
    await firstValueFrom(this.store.updateInvoice(this.invoiceData.id, { pdf: pdfData.filename }));
  }

  private downloadFile(blob: Blob, filename: string): void {
    const fileUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(fileUrl);
  }

  private errorDetail(error: any): string {
    const message = error?.error?.message
      ?? error?.error?.error
      ?? error?.message;
    return message ? ` ${message}` : '';
  }

  private async processXML(xml: any): Promise<any> {
    this.parser = new xml2js.Parser({ trim: true, explicitArray: true });
    return this.parser.parseStringPromise(xml);
  }

  /*async getBase64ImageFromUrl(imageUrl: any) {
    var res = await fetch(imageUrl);
    var blob = await res.blob();

    console.log("BLOB => ", blob);
  
    return new Promise((resolve, reject) => {
      var reader  = new FileReader();
      reader.addEventListener("load", function () {
          resolve(reader.result);
      }, false);
  
      reader.onerror = () => {
        return reject(this);
      };
      reader.readAsDataURL(blob);
    })
  }*/

  openModal(modalContent: TemplateRef<any>) {
    this.buildInvoiceComplementForm();
    this.loadComplementPayments();
    this.modalRef = this.modalService.open(modalContent, { backdrop: 'static', keyboard: false });
  }

  dismiss() {
    if (this.modalRef) {
      const modalRef = this.modalRef;
      this.modalRef = undefined;
      this.updateDismiss.emit();
      modalRef.dismiss();
    }
  }

}
